import { useMutation, useQuery } from "@tanstack/react-query";
import { Bot, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { Shell } from "./Login";

import { t } from "@/lib/i18n";
/** Zustimmung: ein KI-Assistent (z. B. Claude) möchte im Namen der angemeldeten Person Daten eines Kunden lesen */
export function ConnectAuthorizePage() {
  const [params] = useSearchParams();
  const requestId = params.get("request") ?? "";
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["oauth-request", requestId], queryFn: () => api.mcp.request(requestId), retry: false, enabled: !!requestId });
  const [tenantId, setTenantId] = useState("");
  const status = q.error instanceof ApiError ? q.error.status : null;
  // nicht angemeldet → Anmeldung, danach zurück hierher
  useEffect(() => {
    if (status === 401) navigate(`/login?next=${encodeURIComponent(`/connect/authorize?request=${requestId}`)}`, { replace: true });
  }, [status, navigate, requestId]);
  useEffect(() => {
    const first = q.data?.tenants.find((tv) => tv.eligible);
    if (first && !tenantId) setTenantId(first.id);
  }, [q.data, tenantId]);
  const decide = useMutation({
    mutationFn: (approve: boolean) => api.mcp.decide(requestId, { approve, ...(approve ? { tenantId } : {}) }),
    onSuccess: (r) => { window.location.href = r.redirect; },
  });
  const err = decide.error instanceof ApiError ? (decide.error.body as { message?: string })?.message : null;

  return (
    <Shell>
      {!requestId || status === 404 ? (
        <p className="text-[14px] text-fg-muted text-center">{t("Diese Anfrage ist abgelaufen. Bitte im Assistenten erneut auf „Verbinden“ klicken.")}</p>
      ) : !q.data ? (
        <p className="text-[14px] text-fg-muted text-center">{t("Einen Moment …")}</p>
      ) : (
        <div>
          <div className="mx-auto size-12 rounded-2xl bg-surface-2 grid place-items-center"><Bot className="size-6 text-fg-muted" /></div>
          <h1 className="mt-4 text-[20px] font-semibold text-center tracking-tight">„{q.data.client}{t("“ mit Logiful verbinden?")}</h1>
          <p className="mt-2 text-[13.5px] text-fg-muted text-center">
            {t("Der Assistent darf Daten des gewählten Kunden")} <b className="text-fg">{t("lesen")}</b> {t("– Aufträge, Bestände, Wareneingänge, Retouren, Handlungsbedarf – und zwar nur, was du auch im Portal sehen darfst. Er kann nichts ändern.")}
          </p>
          <label className="mt-5 block">
            <span className="text-[12px] font-medium text-fg-muted">{t("Kunde")}</span>
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-border bg-surface px-3 text-[14px]">
              {q.data.tenants.map((tv) => <option key={tv.id} value={tv.id} disabled={!tv.eligible}>{tv.name}{tv.eligible ? "" : t(" – nicht freigeschaltet")}</option>)}
            </select>
          </label>
          {!q.data.tenants.some((tv) => tv.eligible) && <p className="mt-2 text-[12.5px] text-warning">{t("Das Modul „KI-Assistent“ ist für keinen deiner Kunden freigeschaltet – bitte bei Logiful melden.")}</p>}
          {err && <p className="mt-2 text-[12.5px] text-danger">{err}</p>}
          <div className="mt-5 flex gap-2">
            <Button className="flex-1" variant="ghost" loading={decide.isPending && decide.variables === false} onClick={() => decide.mutate(false)}>{t("Ablehnen")}</Button>
            <Button className="flex-1" variant="primary" icon={<ShieldCheck className="size-4" />} disabled={!tenantId} loading={decide.isPending && decide.variables === true} onClick={() => decide.mutate(true)}>{t("Zulassen")}</Button>
          </div>
          <p className="mt-4 text-[12px] text-fg-subtle text-center">{t("Weiterleitung zu")} {q.data.redirectHost}{t(". Jederzeit trennbar unter Einstellungen → KI-Assistent.")}</p>
        </div>
      )}
    </Shell>
  );
}
